/**
 * SimpleMountGuard - Lightweight StrictMode-safe mount/unmount management
 * 
 * Focuses on preventing duplicate initialization without interfering with normal operation.
 */

import { useState, useEffect } from 'react';

export class SimpleMountGuard {
    constructor(name = 'SimpleMountGuard') {
        this.name = name;
        this.instanceId = this.generateInstanceId();
        this.isMounted = false;
        this.cleanupFunctions = [];
        
        // Development logging
        this.isDev = process.env.NODE_ENV === 'development';
        
        if (this.isDev) {
            console.log(`🛡️ ${this.name}: Created (${this.instanceId})`);
        }
    }

    /**
     * Generate a unique instance ID
     */
    generateInstanceId() {
        return `mount_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    }

    /**
     * Check if this instance is still current (not stale)
     */
    isCurrent() {
        return this.isMounted;
    }

    /**
     * Mark as mounted and return the instance ID
     */
    mount() {
        if (!this.isMounted) {
            this.isMounted = true;
            this.instanceId = this.generateInstanceId();
            
            if (this.isDev) {
                console.log(`🛡️ ${this.name}: Mounted (${this.instanceId})`);
            }
        }
        
        return this.instanceId;
    }

    /**
     * Mark as unmounted and cleanup all resources
     */
    unmount() {
        if (!this.isMounted) {
            return;
        }
        
        this.isMounted = false;
        
        if (this.isDev) {
            console.log(`🛡️ ${this.name}: Unmounting (${this.instanceId})`);
        }
        
        // Execute all cleanup functions
        this.cleanupFunctions.forEach(cleanup => {
            try {
                cleanup();
            } catch (error) {
                console.warn(`🛡️ ${this.name}: Cleanup function failed:`, error);
            }
        });
        this.cleanupFunctions = [];
        
        if (this.isDev) {
            console.log(`🛡️ ${this.name}: Cleanup complete (${this.instanceId})`);
        }
    }

    /**
     * Add a cleanup function to be called on unmount
     */
    addCleanup(cleanupFunction) {
        if (typeof cleanupFunction === 'function') {
            this.cleanupFunctions.push(cleanupFunction);
        }
    }

    /**
     * Execute a function only if the guard is still current
     */
    executeIfCurrent(fn) {
        if (this.isCurrent()) {
            return fn();
        }
        return null;
    }

    /**
     * Execute an async function only if the guard is still current
     */
    async executeIfCurrentAsync(fn) {
        if (this.isCurrent()) {
            return await fn();
        }
        return null;
    }

    /**
     * Get current instance ID
     */
    getInstanceId() {
        return this.instanceId;
    }

    /**
     * Get mount status
     */
    getMountStatus() {
        return {
            isMounted: this.isMounted,
            instanceId: this.instanceId,
            trackedCleanupFunctions: this.cleanupFunctions.length
        };
    }
}

/**
 * Hook for using SimpleMountGuard in React components
 */
export function useSimpleMountGuard(name) {
    const [mountGuard] = useState(() => new SimpleMountGuard(name));
    
    useEffect(() => {
        const instanceId = mountGuard.mount();
        
        return () => {
            mountGuard.unmount();
        };
    }, [mountGuard]);
    
    return mountGuard;
}

